/**
 * Platform-console entry point.
 *
 * Provider tree (outermost → innermost):
 *   AppThemeProvider   — @obrava/ui ThemeProvider (light/dark, prefers-color-scheme
 *                        + manual toggle); emits the CSS-var stylesheet once.
 *   AppI18nProvider    — shared createI18n() wired into react-i18next, default 'ca'.
 *   AppQueryProvider   — a single React Query client.
 *   AuthProvider       — the separate console auth realm (platform_users + MFA).
 *   RouterProvider     — the code-based TanStack Router tree; AuthGate guards the
 *                        authenticated routes on user && platformUser && mfaPassed.
 *
 * Router sits innermost so every routed screen can read theme, i18n, query and
 * auth context. AppUpdateNotice (the «new version» popup) sits beside the query tree: it
 * needs only theme + i18n, so it also reaches the sign-in screen.
 */

import React from 'react';
import '@obrava/ui/fonts';
import { createRoot } from 'react-dom/client';
import { RouterProvider } from '@tanstack/react-router';
import { AppThemeProvider } from './providers/ThemeProvider.js';
import { AppI18nProvider } from './providers/I18nProvider.js';
import { AppQueryProvider } from './providers/QueryProvider.js';
import { AuthProvider } from './auth/AuthProvider.js';
import { AppUpdateNotice } from './components/AppUpdateNotice.js';
import { router } from './router.js';

function App(): React.ReactElement {
  return (
    <AppThemeProvider>
      <AppI18nProvider>
        <AppQueryProvider>
          <AuthProvider>
            <RouterProvider router={router} />
          </AuthProvider>
        </AppQueryProvider>
        <AppUpdateNotice />
      </AppI18nProvider>
    </AppThemeProvider>
  );
}

const el = document.getElementById('root');
if (el) {
  createRoot(el).render(
    <React.StrictMode>
      <App />
    </React.StrictMode>,
  );
}
